#!/usr/bin/env node

/**
 * Brick Codegen CLI
 * Simple code generation for Brick modules
 */

import { program } from "@commander-js/extra-typings";
import { readFileSync } from "fs";
import { dirname, join } from "path";
import pc from "picocolors";
import { fileURLToPath } from "url";
import { BrickCodegen } from "./index.js";
import type { BrickCodegenConfig } from "./main-index.js";

const __filename = fileURLToPath(import.meta.url);
const __dirname = dirname(__filename);

// Read package.json for version
const packageJson = JSON.parse(
  readFileSync(join(__dirname, "../package.json"), "utf-8")
);

interface CLIOptions {
  debug?: boolean;
  outputIos?: string;
  outputAndroid?: string;
  platform?: string;
}

// Logging utility
function log(message: string): void {
  console.log(`${pc.bold(pc.green("[Brick]"))} ${message}`);
}

function logError(message: string): void {
  console.error(`${pc.bold(pc.green("[Brick]"))} ${pc.red(message)}`);
}

program
  .name("brick-codegen")
  .description(
    "Brick Module Framework - React Native TurboModule Code Generator"
  )
  .version(packageJson.version)
  .option("--debug", "Enable debug logging", false)
  .option("--output-ios <path>", "Custom iOS output directory")
  .option("--output-android <path>", "Custom Android output directory")
  .option("--platform <platform>", "Target platform: ios or android");

// Default action - just generate
program.action(async (options: CLIOptions) => {
  await runGenerate(options);
});

async function runGenerate(options: CLIOptions) {
  try {
    const config = await buildConfig(options);
    const codegen = new BrickCodegen(config);

    const result = await codegen.generate();

    if (result.success) {
      log("Done.");
    } else {
      if (result.errors.length > 0) {
        for (const error of result.errors) {
          logError(error);
        }
      }
      process.exit(1);
    }
  } catch (error) {
    logError(`${error instanceof Error ? error.message : "Unknown error"}`);
    process.exit(1);
  }
}

async function buildConfig(options: CLIOptions): Promise<BrickCodegenConfig> {
  const projectRoot = process.cwd();

  // Parse platform selection
  const platforms = parsePlatformOptions(options);

  return {
    projectRoot,
    outputIos: options.outputIos,
    outputAndroid: options.outputAndroid,
    debug: options.debug || false,
    platforms,
  };
}

function parsePlatformOptions(options: CLIOptions): string[] | undefined {
  if (!options.platform) {
    return undefined; // Generate for all platforms
  }

  const platform = options.platform.trim().toLowerCase();
  
  // Validate platform
  if (!['ios', 'android'].includes(platform)) {
    logError(`Invalid platform: ${platform}. Supported platforms: ios, android`);
    process.exit(1);
  }
  
  return [platform];
}

// Handle uncaught exceptions gracefully
process.on("uncaughtException", (error) => {
  logError(`Uncaught exception: ${error.message}`);
  process.exit(1);
});

process.on("unhandledRejection", (reason) => {
  logError(`Unhandled rejection: ${reason}`);
  process.exit(1);
});

// Parse CLI arguments
program.parse();
